import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, CircularProgress, Modal, useTheme } from "@mui/material";
import "./MemberCard.css";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useWeb3Auth } from "../../hooks/useWeb3Auth";
import { isMobile, isIOS, isAndroid } from "react-device-detect";
import {
  API_POST_IOS_WALLET_LINK,
  API_POST_GOOGLE_WALLET_LINK,
  getProticoClient,
} from "../../utils/api";
import { MintedNFT } from "../../hooks/useMintedNftList";

// const fakeMintedNFTDetail = {
//   id: 3,
//   title: "Protico Chatting Chip - Next Level! (PCC)",
//   name: "Protico - Chatting Chip Level2 #998",
//   description: "Time to Level Up!",
//   image:
//     "https://d1q39w3eq2bx7b.cloudfront.net/iota/chatting_chip_v2.png",
//   contract: "0x98F0b779F75E0389704d70276c267DEd46941e6f",
//   owner: "0x72b3821a315FE22e85be455b0B963a02c82D248d",
//   token_id: 998,
//   attributes: [
//     {
//       type: "Body",
//       wave: 1,
//       code: "T",
//       levelUp: "MSG",
//     },
//   ],
// };

interface WalletLinkResponse {
  url: string;
}

interface IProps {
  setIsCardDetailOpen: any;
  nft: MintedNFT;
}

const MemberCardDetail: React.FC<IProps> = ({ setIsCardDetailOpen, nft }) => {
  const { t } = useTranslation();
  const { address: userAddress } = useWeb3Auth();

  const handleIOSWalletButtonClicked = async () => {
    try {
      const response = await getProticoClient().api.post(
        API_POST_IOS_WALLET_LINK,
        {
          minted_nft: nft.id,
        }
      );
      const data = response.data as WalletLinkResponse;
      const url = data.url;
      window.open(url, "_blank");
    } catch (err) {
      return "";
    }
  };

  const handleGoogleWalletButtonClicked = async () => {
    try {
      const response = await getProticoClient().api.post(
        API_POST_GOOGLE_WALLET_LINK,
        {
          minted_nft: nft.id,
        }
      );
      const data = response.data as WalletLinkResponse;
      const url = data.url;
      window.open(url, "_blank");
    } catch (err) {
      return "";
    }
  };

  return (
    <Modal
      open={true}
      onClose={() => {
        setIsCardDetailOpen(false);
      }}
      style={{
        background: "#111111e0",
        border: "1px solid #333",
        width: "100vw",
        height: "100vh",
        padding: "12px 16px",
        color: "white",
      }}
    >
      <div className="membercard-detail-container">
        <div className="membercard-detail-topbar">
          <button
            className="membercard-detail-backbutton"
            onClick={() => {
              setIsCardDetailOpen(false);
            }}
          >
            <ArrowBackIcon />
          </button>
        </div>
        <div className="membercard-detail-header">
          <h2>{`${nft.passclass_meta.title}`}</h2>
          <div className="membercard-detail-tokentype">
            <p>ERC-721</p>
          </div>
        </div>
        <div className="membercard-detail-image-wrapper">
          <div className="membercard-detail-image-container">
            <img
              src={
                !!nft.domain_nft_logo
                  ? nft.domain_nft_logo
                  : "https://d1q39w3eq2bx7b.cloudfront.net/iota/chatting_chip_v2.png"
              }
            />
            <div className="membercard-detail-tokenid-badge">
              <h4>{`#${nft.token_id}`}</h4>
            </div>
          </div>
        </div>
        <div className="membercard-detail-wallet-button-group">
          {(!isMobile || isIOS) && (
            <button
              className="membercard-detail-wallet-button"
              onClick={() => {
                if (!isMobile || isIOS) {
                  handleIOSWalletButtonClicked();
                }
              }}
            >
              <img src="images/add-to-apple-wallet-us.svg" />
            </button>
          )}
          {(!isMobile || isAndroid) && (
            <button
              className="membercard-detail-wallet-button"
              onClick={() => {
                if (!isMobile || isAndroid) {
                  handleGoogleWalletButtonClicked();
                }
              }}
            >
              <img src="images/add-to-google-wallet-us.svg" />
            </button>
          )}
        </div>

        <div className="membercard-detail-divider" />

        <div className="membercard-detail-main-description-container">
          <div className="membercard-detail-main-description-label">
            <h3>Owner</h3>
          </div>
          <div className="membercard-detail-main-description-value">
            <a
              href={`https://explorer.evm.iota.org/address/${nft.protico_user_address}`}
              target="_blank"
              rel="noreferrer"
            >
              {nft.protico_user_address}
            </a>
          </div>
          <div className="membercard-detail-main-description-label">
            <h3>Contract</h3>
          </div>
          <div className="membercard-detail-main-description-value">
            <a
              href={`https://explorer.evm.iota.org/token/${nft.contract_address}`}
              target="_blank"
              rel="noreferrer"
            >
              {nft.contract_address}
            </a>
          </div>
          <div className="membercard-detail-main-description-label">
            <h3>Token ID</h3>
          </div>
          <div className="membercard-detail-main-description-value">
            <a
              href={`https://explorer.evm.iota.org/token/${nft.contract_address}/instance/${nft.token_id}`}
              target="_blank"
              rel="noreferrer"
            >
              {nft.token_id}
            </a>
          </div>
        </div>
        <div className="membercard-detail-divider" />
        <div className="membercard-detail-main-description-container">
          <div className="membercard-detail-main-description-label">
            <h3>Name</h3>
          </div>
          <div className="membercard-detail-main-description-value">
            <p>
              {nft.passclass_meta.name_base} #{nft.token_id}
            </p>
          </div>
          <div className="membercard-detail-main-description-label">
            <h3>{nft.passclass_meta.field_a_header}</h3>
          </div>
          <div className="membercard-detail-main-description-value">
            <p>{nft.passclass_meta.field_a_value}</p>
          </div>
          <div className="membercard-detail-main-description-label">
            <h3>{nft.passclass_meta.field_b_header}</h3>
          </div>
          <div className="membercard-detail-main-description-value">
            <p>{nft.passclass_meta.field_b_value}</p>
          </div>
          {/* <div className="membercard-detail-main-description-label">
            <h3>Attributes</h3>
          </div>
          <div className="membercard-detail-main-description-value-bubble-wrapper">
            <div className="membercard-detail-main-description-value-bubble">
              <h4>Type</h4>
              <p>Body</p>
            </div>
            <div className="membercard-detail-main-description-value-bubble">
              <h4>Wave</h4>
              <p>1</p>
            </div>
            <div className="membercard-detail-main-description-value-bubble">
              <h4>Code</h4>
              <p>T</p>
            </div>
            <div className="membercard-detail-main-description-value-bubble">
              <h4>Level Up!</h4>
              <p>MSG</p>
            </div>
          </div> */}
        </div>
      </div>
    </Modal>
  );
};

export default MemberCardDetail;
